<template>
  <div class="chart-container" ref="chartRef" style="width: 100%; height: 100%"></div>
</template>

<script setup>
import * as echarts from "echarts";
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";

// 假设你的 list 是从 props 或父组件传入的，这里先定义一个响应式数据
// 实际使用时可以从 props 接收或 API 获取
const props = defineProps({
  data: {
    type: Array,
    default: () => []
  }
})

// 图表容器和实例
const chartRef = ref(null);
let chartInstance = null;

// 根据 list 数据生成 ECharts 配置
const generateChartOption = (list) => {
  // 空数据处理（保持合法配置，显示“暂无数据”）
  // if (!Array.isArray(list) || list.length === 0) {
  //   return {
  //     title: {
  //       show: true,
  //       text: '暂无数据',
  //       left: 'center',
  //       top: 'center',
  //       textStyle: { color: '#ffffff', fontSize: 14 }
  //     },
  //     xAxis: { type: 'category', data: [], axisLabel: { color: '#ffffff' } },
  //     yAxis: {
  //       type: 'value',
  //       name: '单位（人）',
  //       nameTextStyle: { color: '#ffffff' },
  //       axisLabel: { color: '#ffffff' }
  //     },
  //     series: [],
  //     tooltip: { show: false },
  //     legend: { data: [], textStyle: { color: '#ffffff' } }
  //   };
  // }

  // ---------- 1. 收集所有唯一的通道和 accessWayText ----------
  const channelMap = new Map(); // key: channelId, value: { name, counts: Map<accessWayText, number> }
  const wayTextSet = new Set();  // 存放所有出现的 accessWayText

  list.forEach(item => {
    const channelId = item.channelId;
    const channelName = item.channelName || item.accessWayText;
    const wayText = item.accessWayText;   // 例如 "进"、"出"
    const count = item.detectionCount || 0;

    if (!wayText) return; // 忽略没有 accessWayText 的数据
    wayTextSet.add(wayText);

    if (!channelMap.has(channelId)) {
      channelMap.set(channelId, {
        name: channelName,
        counts: new Map()
      });
    }
    const entry = channelMap.get(channelId);
    const oldCount = entry.counts.get(wayText) || 0;
    entry.counts.set(wayText, oldCount + count);
  });

  // 如果没有有效数据（例如所有 item 都没有 accessWayText）
  if (wayTextSet.size === 0 || channelMap.size === 0) {
    return {
      title: {
        show: true,
        text: '暂无有效数据',
        left: 'center',
        top: 'center',
        textStyle: { color: '#ffffff', fontSize: 14 }
      },
      xAxis: { type: 'category', data: [], axisLabel: { color: '#ffffff' } },
      yAxis: {
        type: 'value',
        name: '单位（人次）',
        nameTextStyle: { color: '#ffffff' },
        axisLabel: { color: '#ffffff' }
      },
      series: [],
      tooltip: { show: false },
      legend: { data: [], textStyle: { color: '#ffffff' } }
    };
  }

  // ---------- 2. 整理通道顺序（按 channelId 排序，保持稳定）----------
  const channels = Array.from(channelMap.entries())
    .sort((a, b) => String(a[0]).localeCompare(String(b[0]))) // 按 channelId 排序
    .map(([id, val]) => ({ id, name: val.name, counts: val.counts }));

  const xAxisData = channels.map(c => c.name);
  const wayTexts = Array.from(wayTextSet).sort(); // 按文本排序，也可以保留原始顺序

  // ---------- 3. 为每个 accessWayText 生成一个 series ----------
  // 预定义一组颜色（可根据需要增减或改为动态生成）
  const colorPalette = ['#5087EC', '#68BBC4', '#F5A623', '#F15A24', '#7ED321', '#BD10E0', '#50E3C2', '#4A90E2'];
  const series = wayTexts.map((wayText, index) => {
    // 提取每个通道下该 wayText 的数量
    const data = channels.map(channel => channel.counts.get(wayText) || 0);
    return {
      name: wayText,
      type: 'bar',
      barWidth: '30',
      data: data,
      itemStyle: { color: colorPalette[index % colorPalette.length] },
      label: {
          show: true,
          position: 'top',
          color: props.fontSizeColor,
          fontSize: 14,
          distance: 5
      }
    };
  });

  // ---------- 4. 返回完整 ECharts 配置 ----------
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    legend: {
      data: wayTexts,
      textStyle: { color: '#ffffff' }
    },
    grid: {
      left: 20,
      right: 10,
      bottom: 10,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisTick: { alignWithLabel: true },
      axisLabel: { color: '#ffffff' }
    },
    yAxis: {
      type: 'value',
      name: '单位（人）',
      nameTextStyle: { color: '#ffffff' },
      axisLabel: { color: '#ffffff' }
    },
    series: series
  };
};

// 初始化或更新图表
const updateChart = () => {
  if (!chartInstance) return;
  const option = generateChartOption(props.data);
  chartInstance.setOption(option, true); // true 表示完全替换配置
};

// 初始化图表
const initChart = () => {
  if (chartRef.value) {
    chartInstance = echarts.init(chartRef.value);
    updateChart(); // 使用数据生成配置
  }
};

// 监听数据变化，重新渲染图表
watch(() => props.data, () => {
  nextTick(() => {
    updateChart();
  });
}, { deep: true });

// 窗口尺寸变化适配
const handleResize = () => {
  chartInstance?.resize();
};

onMounted(() => {
  initChart();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  chartInstance?.dispose();
});
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
  min-height: 0;
}
</style>